import React, { useEffect, useRef, useState } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { WorkoutExercise } from "../workout/types";
import makeStyles from "@mui/styles/makeStyles";

const EXERCISE_INDEX_ATTR = "data-exercise-index";
const SUPERSET_ICON_ATTR = "data-superset-icon";
export const WORKOUT_SECTION_ATTR = "data-workout-section";

interface TitleWithSupersetProps {
  card: WorkoutExercise;
  index: number;
  exercises: WorkoutExercise[];
}

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "flex-start",
    position: "relative",
    overflow: "visible",
  },
  iconColumn: {
    width: 16,
    height: 24,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    flexShrink: 0,
  },
  supersetLine: {
    position: "absolute",
    left: "50%",
    transform: "translateX(-50%) scaleX(1.5)",
    width: 1,
    transformOrigin: "center",
    zIndex: 0,
    transition: "height 0.15s ease-out",
  },
  title: {
    fontSize: 16,
    lineHeight: 1.5,
    fontWeight: "bold",
  },
}));

export const TitleWithSuperset = ({
  card,
  index,
  exercises,
}: TitleWithSupersetProps) => {
  const s = useStyles();
  const theme = useTheme();
  const iconRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [lineHeight, setLineHeight] = useState(0);

  const prev = exercises[index - 1];
  const next = exercises[index + 1];
  const hasPrev = !!(card?.superset && prev?.superset === card.superset);
  const hasNext = !!(card?.superset && next?.superset === card.superset);
  const isPartOfGroup = hasPrev || hasNext;

  useEffect(() => {
    if (!hasNext || !containerRef.current) return;
    const root =
      containerRef.current.closest(`[${WORKOUT_SECTION_ATTR}]`) ||
      containerRef.current.parentElement ||
      document;

    const calculateDistance = () => {
      const nextIcon = root.querySelector(
        `[${EXERCISE_INDEX_ATTR}="${index + 1}"] [${SUPERSET_ICON_ATTR}]`,
      ) as HTMLElement;

      if (nextIcon && iconRef.current) {
        const diff =
          nextIcon.getBoundingClientRect().top -
          iconRef.current.getBoundingClientRect().top;
        if (diff > 0) setLineHeight(diff);
      }
    };
    calculateDistance();

    const observer = new ResizeObserver(() =>
      window.requestAnimationFrame(calculateDistance),
    );

    const currentBox = root.querySelector(
      `[${EXERCISE_INDEX_ATTR}="${index}"]`,
    );
    const nextBox = root.querySelector(
      `[${EXERCISE_INDEX_ATTR}="${index + 1}"]`,
    );

    if (currentBox) observer.observe(currentBox);
    if (nextBox) observer.observe(nextBox);

    return () => observer.disconnect();
  }, [hasNext, index]);

  return (
    <Box
      ref={containerRef}
      className={s.container}
      {...{ [EXERCISE_INDEX_ATTR]: index }}
    >
      {isPartOfGroup && (
        <Box
          ref={iconRef}
          className={s.iconColumn}
          {...{ [SUPERSET_ICON_ATTR]: true }}
        >
          {hasPrev && (
            <Box
              className={s.supersetLine}
              sx={{
                top: -20,
                bottom: "50%",
                backgroundColor: theme.palette.primary.main,
              }}
            />
          )}
          {hasNext && (
            <Box
              className={s.supersetLine}
              sx={{
                top: "50%",
                height: lineHeight > 0 ? `${lineHeight}px` : "40px",
                backgroundColor: theme.palette.primary.main,
              }}
            />
          )}
          <RadioButtonUncheckedIcon
            sx={{
              fontSize: 16,
              color: theme.palette.primary.main,
              backgroundColor: theme.palette.background.paper,
              borderRadius: "50%",
              zIndex: 1,
              position: "relative",
            }}
          />
        </Box>
      )}
      <Typography
        className={s.title}
        sx={{
          paddingLeft: isPartOfGroup ? 0 : theme.spacing(2),
        }}
      >
        {card.title}
      </Typography>
    </Box>
  );
};

export default TitleWithSuperset;
